import { Switch as HeroUiSwitch } from '@heroui/react';
import { type FC, memo } from 'react';

type SwitchProps = {
    // Whether the switch is on. Controlled — pair with `onValueChange`.
    isSelected?: boolean;
    onValueChange?: (isSelected: boolean) => void;
    name?: string;
    // Provide either a visible label via `children` or an `aria-label` for screen readers.
    'aria-label'?: string;
    isDisabled?: boolean;
    className?: string;
    // Color of the "on" state. Defaults to the themed `primary` (coral for Quantic, blue for Valar).
    color?: 'primary' | 'black';
    children?: React.ReactNode;
};

// Thumb animation. HeroUI slides the thumb by animating `margin-inline-start` (`ms-5`) and adds a
// press "squish" via width/margin (`w-6`/`ml-4`). Those are all LAYOUT properties, so the browser
// recomputes them on the main thread every frame. That's fine when nothing else is happening (e.g.
// the HeroUI docs, or a toggle whose save is async), but when a toggle ALSO triggers a synchronous
// render — e.g. FilterControls mounting its advanced-filters panel in the same commit — that render
// starves the main thread during the animation window and the thumb visibly stutters. We instead
// move the thumb with `transform`, which the compositor animates on its own thread, so it stays
// smooth regardless of what else the toggle re-renders. Applied to the thumb slot unconditionally
// (see below), so every <Switch>, at any color, gets this.
//
// Each class cancels its HeroUI counterpart via a tailwind-merge dedup:
//   - `ms-0` cancels the base `group-data-[selected=true]:ms-5`; `translate-x-5` (20px = the md
//     thumb's travel) moves it instead. RTL flips the direction.
//   - `w-5` cancels the pressed `w-6` widen; `ml-0` cancels the pressed `ml-4` reposition.
// Each entry is a full literal class string (not a fragment) so the Tailwind scanner picks it up.
//
// Maintenance note: this is coupled to HeroUI's internal toggle slot classes and to the md size
// (`translate-x-5` assumes the 20px md travel). A HeroUI upgrade that renames those classes would
// silently stop cancelling them; Switch.spec.tsx asserts `ms-5`/`w-6`/`ml-4`/`transition-all` stay
// absent from the rendered thumb to catch that.
const thumbClassName = [
    'transition-transform',
    'group-data-[selected=true]:ms-0',
    'ltr:group-data-[selected=true]:translate-x-5',
    'rtl:group-data-[selected=true]:-translate-x-5',
    'group-data-[pressed=true]:w-5',
    'group-data-[selected]:group-data-[pressed]:ml-0',
].join(' ');

export const Switch: FC<SwitchProps> = memo(({ color = 'primary', className, children, ...rest }) => (
    <HeroUiSwitch
        data-id="Switch"
        {...rest}
        className={className}
        classNames={{
            // Unconditional (all colors) — see the thumbClassName comment for the why.
            thumb: thumbClassName,
            // Default the label to normal weight. HeroUI's label slot sets no font-weight, so a `children`
            // label would otherwise inherit whatever weight the surrounding context uses (e.g. a bold
            // filter panel). Consumers can still override by setting a weight on their `children`.
            label: 'font-normal',
            // Track color. The off (unselected) state uses our design-system slate-grey rather than
            // HeroUI's default zinc (`bg-default-200`) so every switch in the app is consistent. The on
            // (selected) state stays the themed `primary` by default; `black` overrides only that.
            wrapper: color === 'black' ? 'bg-slate-grey-200 group-data-[selected=true]:bg-black' : 'bg-slate-grey-200',
        }}
    >
        {children}
    </HeroUiSwitch>
));
